// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { useState, useRef, useCallback } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import * as Types from "../utils/types";

interface GetApiResponse<T> {
  data?: T;
  loading: boolean;
  error: Error | any | null;
  getApiData: (url?: string, headers?: Record<string, string>) => Promise<void>;
}
interface PostApiResponse<T> {
  data?: T;
  loading: boolean;
  error: Error | any | null;
  postApiData: (payload: any, url?: string, headers?: Record<string, string>) => Promise<void>;
}

export const useGetApi = <T>(initialReq: Types.GetApiRequest): GetApiResponse<T> => {
  const { getAccessToken, httpRequest } = useAuthContext();
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Types.GetApiResponseError>();
  const [req, setReq] = useState<Types.GetApiRequest>(initialReq);

  const getApiData = async (url?: string, headers?: Record<string, string>) => {
    const currentReq = {
      url: url || req.url,
      headers: headers || req.headers || {},
    };
    const token = await getAccessToken();
    currentReq.headers['x-jwt-assertion'] = token;
    setLoading(true);
    try {
      const response = await httpRequest({
        url: currentReq.url,
        method: 'GET',
        headers: currentReq.headers,
      });
      // below will get executed only if the above request returns a 2xx response
      setData(response.data);
    } catch (error: any) {
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log("response: ", error.response.data);
        console.log("status: ", error.response.status);
        setError({"message": error.response.data.message, "status": error.response.status});
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser 
        setError({"message": "error while sending the request", "status": 0});
        console.log(error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError({"message": "unexpected error occured while invoking the api", "status": 0});
        console.log('Error: ', error.message);
      }
    } finally {
      setLoading(false);
    }
  };
  return {
    data,
    loading,
    error,
    getApiData
  };
};

export const usePostApi = <T>(initialReq: Types.GetApiRequest): PostApiResponse<T> => {
  const { getAccessToken, httpRequest } = useAuthContext();
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Types.GetApiResponseError>();
  const [req, setReq] = useState<Types.GetApiRequest>(initialReq);

  const postApiData = async (payload: any, url?: string, headers?: Record<string, string>) => {
    const currentReq = {
      url: url || req.url,
      headers: headers || req.headers || {},
    };
    
    const token = await getAccessToken();
    currentReq.headers['x-jwt-assertion'] = token;
    
    if (!currentReq.headers['Content-Type']) {
        currentReq.headers['Content-Type'] = 'application/json';
    }
    if (!currentReq.headers['Accept']) {
        currentReq.headers['Accept'] = 'application/json';
    }

    setLoading(true);
    try {
      const response = await httpRequest({
        url: currentReq.url,
        method: 'POST', 
        data: payload, 
        headers: currentReq.headers,
      });
      // below will get executed only if the above request returns a 2xx response
      setData(response.data);
    } catch (error: any) {

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log("response: ", error.response.data);
        console.log("status: ", error.response.status);
        setError({"message": error.response.data.message, "status": error.response.status});
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser 
        setError({"message": "error while sending the request", "status": 0});
        console.log(error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError({"message": "unexpected error occured while invoking the api", "status": 0});
        console.log('Error: ', error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    postApiData
  };
};

interface ParallelPostApiResponse<T> {
  dataMap: Map<string, T>;
  loading: boolean;
  /** When merge=true, only fetches IDs not already in the map and merges results in. `url` may be a per-item builder for path-param endpoints. */
  postAll: (items: { id: string; payload: any }[], url: string | ((id: string) => string), merge?: boolean) => Promise<void>;
  clearAll: () => void;
}

export const useParallelPostApi = <T>(): ParallelPostApiResponse<T> => {
  const { getAccessToken, httpRequest } = useAuthContext();
  const [dataMap, setDataMap] = useState<Map<string, T>>(new Map());
  const [loading, setLoading] = useState(false);
  const generationRef = useRef(0);
  const dataMapRef = useRef(dataMap);
  dataMapRef.current = dataMap;

  const clearAll = () => setDataMap(new Map());

  const postAll = useCallback(async (items: { id: string; payload: any }[], url: string | ((id: string) => string), merge = false) => {
    // In merge mode, skip IDs already cached — read via ref to avoid stale closure
    const toFetch = merge ? items.filter(({ id }) => !dataMapRef.current.has(id)) : items;
    if (toFetch.length === 0) return;

    const generation = ++generationRef.current;

    const token = await getAccessToken();
    const headers: Record<string, string> = {
      'x-jwt-assertion': token,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };
    setLoading(true);
    try {
      const results = await Promise.all(
        toFetch.map(async ({ id, payload }) => {
          try {
            const itemUrl = typeof url === 'function' ? url(id) : url;
            const response = await httpRequest({ url: itemUrl, method: 'POST', data: payload, headers });
            return { id, data: response.data as T };
          } catch {
            return { id, data: null as unknown as T };
          }
        })
      );
      if (generation !== generationRef.current) return;
      if (merge) {
        setDataMap(prev => {
          const merged = new Map(prev);
          results.forEach(({ id, data }) => { if (data !== null) merged.set(id, data); });
          return merged;
        });
      } else {
        const newMap = new Map<string, T>();
        results.forEach(({ id, data }) => { if (data !== null) newMap.set(id, data); });
        setDataMap(newMap);
      }
    } finally {
      if (generation === generationRef.current) {
        setLoading(false);
      }
    }
  }, [getAccessToken, httpRequest]);

  return { dataMap, loading, postAll, clearAll };
};
